import {useState, useCallback} from 'react'
import {useSnackbar} from 'notistack'
import {getGeneric, postGeneric} from '../../common/constants'

export function usePaceAnalytics() {
  const [isLoading, setIsLoading] = useState(false)
  const [summary, setSummary] = useState(null)
  const [orders, setOrders] = useState([])
  const [orderCount, setOrderCount] = useState(0)
  const [orderRemaining, setOrderRemaining] = useState(0)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const {enqueueSnackbar} = useSnackbar()

  const fetchSummary = useCallback(
    async ({fromDate, toDate, merchantId}) => {
      const params = {fromDate, toDate}
      if (merchantId) params.merchantId = merchantId
      const res = await getGeneric('dynamiccourier/pace/summary', params)
      return res.data?.result || res.data?.data || res.data
    },
    []
  )

  const fetchOrders = useCallback(
    async ({fromDate, toDate, merchantId, offset = 0, limit = 500}) => {
      const params = {fromDate, toDate, offset, limit}
      if (merchantId) params.merchantId = merchantId
      const res = await getGeneric('dynamiccourier/pace/search', params)
      return res.data?.result || res.data?.data || res.data
    },
    []
  )

  const loadData = useCallback(
    async ({fromDate, toDate, merchantId}) => {
      setIsLoading(true)
      setSummary(null)
      setOrders([])
      setOrderCount(0)
      setOrderRemaining(0)
      try {
        const [summaryData, ordersData] = await Promise.all([
          fetchSummary({fromDate, toDate, merchantId}),
          fetchOrders({fromDate, toDate, merchantId, offset: 0, limit: 2000}),
        ])
        setSummary(summaryData)
        setOrders(ordersData?.data || [])
        setOrderCount(ordersData?.count || 0)
        setOrderRemaining(ordersData?.remaining || 0)
      } catch (err) {
        const msg = err.response?.data?.meta?.message || err.message || 'Failed to load analytics'
        enqueueSnackbar(msg, {variant: 'error'})
      } finally {
        setIsLoading(false)
      }
    },
    [fetchSummary, fetchOrders, enqueueSnackbar]
  )

  const loadMoreOrders = useCallback(
    async ({fromDate, toDate, merchantId}) => {
      setIsLoadingMore(true)
      try {
        const ordersData = await fetchOrders({
          fromDate,
          toDate,
          merchantId,
          offset: orders.length,
          limit: 5000,
        })
        const newOrders = ordersData?.data || []
        setOrders((prev) => [...prev, ...newOrders])
        setOrderRemaining(ordersData?.remaining || 0)
      } catch (err) {
        enqueueSnackbar('Failed to load more orders', {variant: 'error'})
      } finally {
        setIsLoadingMore(false)
      }
    },
    [orders.length, fetchOrders, enqueueSnackbar]
  )

  const downloadReport = useCallback(
    async ({fromDate, toDate, merchantId}) => {
      setIsDownloading(true)
      try {
        const body = {
          reportName: 'PACE_ALLOCATION_REPORT',
          fromDate,
          toDate,
          merchantRef: merchantId || "ALL",
        }
        const res = await postGeneric('reports/getAsync', body)
        const url = res.data?.data?.reportUrl
        if (url) {
          window.open(url, '_blank')
          enqueueSnackbar('Report download started', {variant: 'success'})
        } else {
          enqueueSnackbar('Report generation queued. Check reports page.', {variant: 'info'})
        }
      } catch (err) {
        const msg = err.response?.data?.meta?.message || err.message || 'Failed to generate report'
        enqueueSnackbar(msg, {variant: 'error'})
      } finally {
        setIsDownloading(false)
      }
    },
    [enqueueSnackbar]
  )

  return {
    isLoading,
    summary,
    orders,
    orderCount,
    orderRemaining,
    loadData,
    loadMoreOrders,
    isLoadingMore,
    downloadReport,
    isDownloading,
  }
}
